import { useContext, useState, useEffect } from "react";
import { WeeklyContext } from "../../../../context/global/WeeklyContext";
import { DispatchContext } from "../../../../context/domain/planning/DispatchContext";
import { IoAdd } from "react-icons/io5";
import { WorkerList } from "../../../../components/ui";
import {
  formatDate,
  getDateInLocalTZFromDateStr,
} from "../../../../utils/datetime";

/**
 * Component allows users to manage worker assignments for a specific scope.
 * It provides functionality to open a modal for selecting workers, display the list of
 * assigned workers, and update the worker assignments via backend API calls.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.scope - The scope object containing details of the current scope.
 * @param {string} [props.className] - Additional class names for styling the component.
 *
 * @returns {JSX.Element} The rendered component.
 *
 * @example
 * <ScopeWorkersSection scope={scope} className="custom-class" />
 */
export default function ScopeWorkersSection({ scope, className }) {
  const { scopes } = useContext(WeeklyContext);
  const {
    workers,
    dayDetails,
    openWorkerModal,
    handleWorkerModalSubmit,
    isReadOnly,
  } = useContext(DispatchContext);
  const [usedWorkers, setUsedWorkers] = useState([]);
  const [selectedWorkerIDs, setSelectedWorkerIds] = useState(
    scope.worker_assignment.map((worker) => worker.id)
  );
  const [prevWorkerIDs, setPrevWorkerIds] = useState(selectedWorkerIDs);

  // Force update of selected workers as other components can change the worker list
  useEffect(() => {
    setSelectedWorkerIds(scope.worker_assignment.map((worker) => worker.id));
  }, [scope]);

  useEffect(() => {
    if (dayDetails) {
      const day = dayDetails.find(
        (day) =>
          day.date ===
          formatDate(getDateInLocalTZFromDateStr(scope.date), "YYYY-MM-DD")
      );
      if (day) {
        setUsedWorkers([
          ...scopes
            .filter((s) => s.date === scope.date && !s.skipped)
            .flatMap((s) => s.worker_assignment),
          ...day.off_work,
        ]);
      }
    }
  }, [dayDetails, scope, workers, scopes]);

  /**
   * Open the shared worker picker for this scope's current assignment.
   *
   * @param {void}
   * @returns {void}
   */
  const handleOpenModal = () => {
    openWorkerModal({
      workers,
      onSubmit: (submittedWorkerIds) =>
        handleWorkerModalSubmit(
          scope.id,
          selectedWorkerIDs,
          submittedWorkerIds,
          setPrevWorkerIds,
          setSelectedWorkerIds
        ),
      date: getDateInLocalTZFromDateStr(scope.date),
      scope,
      usedWorkers,
      selectedWorkerIds: selectedWorkerIDs,
    });
  };

  return (
    <div className={`${className} flex flex-col text-base-600`}>
      <div
        className={
          "flex flex-row justify-between border-b border-solid border-gray-300 mb-[2px]"
        }
      >
        <div className={"text-black mx-2"}>Workers</div>
        {!isReadOnly && (
          <button onClick={handleOpenModal} className={"text-base mr-2"}>
            <IoAdd size={20} />
          </button>
        )}
      </div>
      <div className={"flex flex-row"}>
        <button
          onClick={handleOpenModal}
          className={"grow text-gray-600 text-left"}
        >
          <WorkerList
            workers={scope.worker_assignment}
            prevWorkers={
              prevWorkerIDs && workers
                ? workers.filter((worker) => prevWorkerIDs.includes(worker.id))
                : []
            }
          />
        </button>
      </div>
    </div>
  );
}
